/**
 * C-Level Audit & Compliance Intelligence Dashboard
 * FERP UNICORN 5: Executive-facing compliance and audit intelligence
 * First-to-market: Willful ignorance prevention + board-level reporting
 */

import React, { useState } from 'react';
import {
  Card,
  Stack,
  Group,
  Text,
  Tabs,
  Badge,
  Button,
  Grid,
  RingProgress,
  Table,
  Alert,
  ThemeIcon,
  Progress,
  Modal,
  Checkbox,
  Textarea
} from '@mantine/core';
import {
  IconShieldCheck,
  IconAlertTriangle,
  IconFileText,
  IconBuildingBank,
  IconShield,
  IconDownload,
  IconRefresh,
  IconCheck,
  IconX,
  IconAlertCircle
} from '@tabler/icons-react';
import { useExecutiveAudit, type WillfulIgnoranceRisk } from '../hooks/useExecutiveAudit';

export interface CLevelAuditDashboardProps {
  timeRange: { start: Date; end: Date };
}

function getImpactColor(level: string): string {
  switch (level) {
    case 'CRITICAL': return 'red';
    case 'HIGH': return 'orange';
    case 'MEDIUM': return 'yellow';
    case 'LOW': return 'blue';
    default: return 'gray';
  }
}

function formatCurrency(amount: number): string {
  return new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: 'USD',
    minimumFractionDigits: 0
  }).format(amount);
}

export function CLevelAuditDashboard({ timeRange }: CLevelAuditDashboardProps) {
  const {
    impactMatrix,
    willfulIgnoranceRisks,
    boardMetrics,
    regulatoryPreparedness,
    fiduciaryResponsibilities,
    loading,
    refetch
  } = useExecutiveAudit(timeRange);

  const [activeTab, setActiveTab] = useState<string | null>('impact');
  const [acknowledgmentModal, setAcknowledgmentModal] = useState<WillfulIgnoranceRisk | null>(null);
  const [acknowledged, setAcknowledged] = useState(false);
  const [signature, setSignature] = useState('');

  const handleAcknowledge = () => {
    // In production, this would call a mutation to record the acknowledgment
    console.log('[Executive Audit] Acknowledged risk:', acknowledgmentModal?.issue);
    setAcknowledgmentModal(null);
    setAcknowledged(false);
    setSignature('');
  };

  const handleExportReport = () => {
    // In production, this would generate a PDF report
    console.log('[Executive Audit] Exporting board report');
  };

  return (
    <Card shadow="sm" padding="lg" radius="md" withBorder>
      <Stack gap="md">
        {/* Header */}
        <Group justify="space-between">
          <div>
            <Text size="lg" fw={600}>C-Level Audit & Compliance Intelligence</Text>
            <Text size="xs" c="dimmed">Executive Dashboard - Board-Ready Reporting</Text>
          </div>
          <Group gap="xs">
            <Button
              size="xs"
              variant="light"
              onClick={() => refetch()}
              loading={loading}
              leftSection={<IconRefresh size={14} />}
            >
              Refresh
            </Button>
            <Button
              size="xs"
              color="blue"
              leftSection={<IconDownload size={14} />}
              onClick={handleExportReport}
            >
              Export Board Report
            </Button>
          </Group>
        </Group>

        {/* Quick Metrics */}
        <Grid>
          <Grid.Col span={3}>
            <Card padding="md" withBorder>
              <Stack gap="xs">
                <Text size="xs" c="dimmed">Total Workflows</Text>
                <Text size="xl" fw={700}>{boardMetrics?.totalWorkflows || 0}</Text>
                <Badge size="xs" color="red">
                  {boardMetrics?.lifeCriticalWorkflows || 0} Life-Critical
                </Badge>
              </Stack>
            </Card>
          </Grid.Col>

          <Grid.Col span={3}>
            <Card padding="md" withBorder>
              <Stack gap="xs">
                <Text size="xs" c="dimmed">Compliance Score</Text>
                <Text size="xl" fw={700}>{Math.round(boardMetrics?.complianceScore || 0)}%</Text>
                <Progress
                  value={boardMetrics?.complianceScore || 0}
                  color={
                    (boardMetrics?.complianceScore || 0) >= 90 ? 'green' :
                    (boardMetrics?.complianceScore || 0) >= 70 ? 'yellow' : 'red'
                  }
                  size="sm"
                />
              </Stack>
            </Card>
          </Grid.Col>

          <Grid.Col span={3}>
            <Card padding="md" withBorder>
              <Stack gap="xs">
                <Text size="xs" c="dimmed">Audit Trail Integrity</Text>
                <Text size="xl" fw={700}>{Math.round(boardMetrics?.auditTrailIntegrity || 0)}%</Text>
                <Badge
                  size="xs"
                  color={(boardMetrics?.auditTrailIntegrity || 0) >= 99 ? 'green' : 'orange'}
                >
                  {(boardMetrics?.auditTrailIntegrity || 0) >= 99 ? 'Excellent' : 'Review Needed'}
                </Badge>
              </Stack>
            </Card>
          </Grid.Col>

          <Grid.Col span={3}>
            <Card padding="md" withBorder>
              <Stack gap="xs">
                <Text size="xs" c="dimmed">Insurance Liability</Text>
                <Text size="xl" fw={700}>{formatCurrency(boardMetrics?.insuranceLiability || 0)}</Text>
                <Text size="xs" c="dimmed">Exposure</Text>
              </Stack>
            </Card>
          </Grid.Col>
        </Grid>

        {/* Tabs */}
        <Tabs value={activeTab} onChange={setActiveTab}>
          <Tabs.List>
            <Tabs.Tab value="impact" leftSection={<IconAlertTriangle size={16} />}>
              Impact Matrix
            </Tabs.Tab>
            <Tabs.Tab value="willful" leftSection={<IconAlertCircle size={16} />}>
              Willful Ignorance
              {willfulIgnoranceRisks.length > 0 && (
                <Badge size="xs" color="red" ml={4}>{willfulIgnoranceRisks.length}</Badge>
              )}
            </Tabs.Tab>
            <Tabs.Tab value="board" leftSection={<IconFileText size={16} />}>
              Board Report
            </Tabs.Tab>
            <Tabs.Tab value="regulatory" leftSection={<IconBuildingBank size={16} />}>
              Regulatory
            </Tabs.Tab>
            <Tabs.Tab value="insurance" leftSection={<IconShield size={16} />}>
              Insurance
            </Tabs.Tab>
          </Tabs.List>

          {/* Impact Matrix Tab */}
          <Tabs.Panel value="impact" pt="md">
            <Stack gap="md">
              <Alert color="blue" icon={<IconAlertTriangle size={16} />}>
                <Text size="sm">
                  Impact Matrix shows financial and operational risk across all workflows.
                </Text>
              </Alert>

              {impactMatrix.length > 0 ? (
                <Table>
                  <Table.Thead>
                    <Table.Tr>
                      <Table.Th>Workflow</Table.Th>
                      <Table.Th>Impact Level</Table.Th>
                      <Table.Th>Financial Impact</Table.Th>
                      <Table.Th>Affected Users</Table.Th>
                      <Table.Th>Dependencies</Table.Th>
                    </Table.Tr>
                  </Table.Thead>
                  <Table.Tbody>
                    {impactMatrix.map(entry => (
                      <Table.Tr key={entry.workflowId}>
                        <Table.Td>
                          <Text size="sm" fw={500}>{entry.workflowName}</Text>
                          <Text size="xs" c="dimmed">{entry.regulatoryRisk}</Text>
                        </Table.Td>
                        <Table.Td>
                          <Badge color={getImpactColor(entry.impactLevel)}>
                            {entry.impactLevel}
                          </Badge>
                        </Table.Td>
                        <Table.Td>{formatCurrency(entry.financialImpact)}</Table.Td>
                        <Table.Td>{entry.affectedUsers.toLocaleString()}</Table.Td>
                        <Table.Td>
                          <Badge size="xs" color="gray" variant="light">
                            {entry.dependencies.length} workflows
                          </Badge>
                        </Table.Td>
                      </Table.Tr>
                    ))}
                  </Table.Tbody>
                </Table>
              ) : (
                <Alert color="green" icon={<IconCheck size={16} />}>
                  <Text size="sm">No high-impact workflows detected.</Text>
                </Alert>
              )}
            </Stack>
          </Tabs.Panel>

          {/* Willful Ignorance Tab */}
          <Tabs.Panel value="willful" pt="md">
            <Stack gap="md">
              <Alert color="red" icon={<IconAlertTriangle size={16} />}>
                <Text size="sm" fw={600}>Legal Protection: Willful Ignorance Prevention</Text>
                <Text size="xs">
                  These risks require executive acknowledgment to prove due diligence in case of legal proceedings.
                </Text>
              </Alert>

              {willfulIgnoranceRisks.length > 0 ? (
                <Stack gap="sm">
                  {willfulIgnoranceRisks.map((risk, index) => (
                    <Card key={index} padding="md" withBorder>
                      <Stack gap="xs">
                        <Group justify="space-between">
                          <Group gap="xs">
                            <ThemeIcon size="lg" color={getImpactColor(risk.severity)} variant="light">
                              <IconAlertCircle size={20} />
                            </ThemeIcon>
                            <div>
                              <Text size="sm" fw={600}>{risk.issue}</Text>
                              <Text size="xs" c="dimmed">Deadline: {risk.deadline}</Text>
                            </div>
                          </Group>
                          <Badge color={getImpactColor(risk.severity)}>
                            {risk.severity}
                          </Badge>
                        </Group>

                        <Text size="sm">
                          <strong>Consequences:</strong> {risk.consequences}
                        </Text>

                        <Text size="sm">
                          <strong>Mitigation:</strong> {risk.mitigation}
                        </Text>

                        {risk.acknowledgmentRequired && (
                          <Group justify="flex-end">
                            <Button
                              size="xs"
                              color="red"
                              onClick={() => setAcknowledgmentModal(risk)}
                            >
                              Acknowledge Risk
                            </Button>
                          </Group>
                        )}
                      </Stack>
                    </Card>
                  ))}
                </Stack>
              ) : (
                <Alert color="green" icon={<IconCheck size={16} />}>
                  <Text size="sm">No pending acknowledgments. All risks have been addressed.</Text>
                </Alert>
              )}
            </Stack>
          </Tabs.Panel>

          {/* Board Report Tab */}
          <Tabs.Panel value="board" pt="md">
            <Stack gap="md">
              <Card padding="md" withBorder>
                <Stack gap="md">
                  <Text size="lg" fw={600} ta="center">
                    Executive Summary - Board of Directors
                  </Text>

                  <Grid>
                    <Grid.Col span={4}>
                      <div style={{ textAlign: 'center' }}>
                        <RingProgress
                          size={120}
                          thickness={12}
                          sections={[{ value: boardMetrics?.complianceScore || 0, color: 'green' }]}
                          label={
                            <Text size="lg" fw={700} ta="center">
                              {Math.round(boardMetrics?.complianceScore || 0)}%
                            </Text>
                          }
                        />
                        <Text size="xs" c="dimmed" mt="xs">Compliance</Text>
                      </div>
                    </Grid.Col>

                    <Grid.Col span={4}>
                      <div style={{ textAlign: 'center' }}>
                        <RingProgress
                          size={120}
                          thickness={12}
                          sections={[{ value: boardMetrics?.auditTrailIntegrity || 0, color: 'blue' }]}
                          label={
                            <Text size="lg" fw={700} ta="center">
                              {Math.round(boardMetrics?.auditTrailIntegrity || 0)}%
                            </Text>
                          }
                        />
                        <Text size="xs" c="dimmed" mt="xs">Audit Integrity</Text>
                      </div>
                    </Grid.Col>

                    <Grid.Col span={4}>
                      <div style={{ textAlign: 'center' }}>
                        <RingProgress
                          size={120}
                          thickness={12}
                          sections={[{ value: (fiduciaryResponsibilities?.fulfilled || 0), color: 'violet' }]}
                          label={
                            <Text size="lg" fw={700} ta="center">
                              {Math.round(fiduciaryResponsibilities?.fulfilled || 0)}%
                            </Text>
                          }
                        />
                        <Text size="xs" c="dimmed" mt="xs">Fiduciary Duties</Text>
                      </div>
                    </Grid.Col>
                  </Grid>

                  <Button
                    fullWidth
                    color="blue"
                    leftSection={<IconDownload size={16} />}
                    onClick={handleExportReport}
                  >
                    Export One-Page Board Report (PDF)
                  </Button>
                </Stack>
              </Card>
            </Stack>
          </Tabs.Panel>

          {/* Regulatory Tab */}
          <Tabs.Panel value="regulatory" pt="md">
            <Stack gap="md">
              {regulatoryPreparedness.map((prep) => (
                <Card key={prep.framework} padding="md" withBorder>
                  <Stack gap="xs">
                    <Group justify="space-between">
                      <Text size="sm" fw={600}>{prep.framework}</Text>
                      <Group gap="xs">
                        <Badge
                          color={
                            prep.readinessScore >= 90 ? 'green' :
                            prep.readinessScore >= 70 ? 'yellow' : 'red'
                          }
                        >
                          {Math.round(prep.readinessScore)}% Ready
                        </Badge>
                        <Text size="xs" c="dimmed">
                          Next Audit: {prep.nextAuditDate}
                        </Text>
                      </Group>
                    </Group>

                    <Progress
                      value={prep.readinessScore}
                      color={
                        prep.readinessScore >= 90 ? 'green' :
                        prep.readinessScore >= 70 ? 'yellow' : 'red'
                      }
                    />

                    {prep.gaps.length > 0 && (
                      <Stack gap={4} mt="xs">
                        <Text size="xs" fw={500}>{prep.gaps.length} Gaps Remaining:</Text>
                        {prep.gaps.slice(0, 3).map((gap, i) => (
                          <Text key={i} size="xs" c="dimmed">
                            • {gap.requirement} - Due: {gap.deadline}
                          </Text>
                        ))}
                      </Stack>
                    )}
                  </Stack>
                </Card>
              ))}
            </Stack>
          </Tabs.Panel>

          {/* Insurance Tab */}
          <Tabs.Panel value="insurance" pt="md">
            <Stack gap="md">
              <Card padding="md" withBorder>
                <Stack gap="xs">
                  <Group justify="space-between">
                    <Text size="sm" fw={600}>Cyber Insurance Coverage</Text>
                    <Badge color="blue">Active</Badge>
                  </Group>
                  <Grid>
                    <Grid.Col span={6}>
                      <Text size="xs" c="dimmed">Coverage Limit</Text>
                      <Text size="lg" fw={600}>$500,000</Text>
                    </Grid.Col>
                    <Grid.Col span={6}>
                      <Text size="xs" c="dimmed">Current Exposure</Text>
                      <Text size="lg" fw={600}>{formatCurrency(boardMetrics?.insuranceLiability || 0)}</Text>
                    </Grid.Col>
                  </Grid>

                  <Alert color="green" icon={<IconCheck size={16} />}>
                    <Text size="xs">
                      Liability exposure is within coverage limits. Premium reduction opportunities available.
                    </Text>
                  </Alert>
                </Stack>
              </Card>
            </Stack>
          </Tabs.Panel>
        </Tabs>
      </Stack>

      {/* Acknowledgment Modal */}
      <Modal
        opened={acknowledgmentModal !== null}
        onClose={() => setAcknowledgmentModal(null)}
        title="Acknowledge Risk"
        size="lg"
      >
        {acknowledgmentModal && (
          <Stack gap="md">
            <Alert color="red" icon={<IconAlertTriangle size={16} />}>
              <Text size="sm" fw={600}>Legal Disclaimer</Text>
              <Text size="xs">
                This acknowledgment creates an immutable audit trail proving you were aware of this risk.
                This protects against claims of willful ignorance in legal proceedings.
              </Text>
            </Alert>

            <Card padding="md" withBorder>
              <Stack gap="xs">
                <Text size="sm" fw={600}>{acknowledgmentModal.issue}</Text>
                <Text size="xs">
                  <strong>Severity:</strong> {acknowledgmentModal.severity}
                </Text>
                <Text size="xs">
                  <strong>Consequences:</strong> {acknowledgmentModal.consequences}
                </Text>
                <Text size="xs">
                  <strong>Mitigation:</strong> {acknowledgmentModal.mitigation}
                </Text>
              </Stack>
            </Card>

            <Checkbox
              label="I acknowledge this risk and understand the consequences"
              checked={acknowledged}
              onChange={(e) => setAcknowledged(e.currentTarget.checked)}
            />

            <Textarea
              label="Digital Signature"
              placeholder="Type your full name to sign"
              value={signature}
              onChange={(e) => setSignature(e.currentTarget.value)}
              required
            />

            <Group justify="flex-end">
              <Button variant="default" onClick={() => setAcknowledgmentModal(null)}>
                Cancel
              </Button>
              <Button
                color="red"
                onClick={handleAcknowledge}
                disabled={!acknowledged || !signature}
              >
                Sign & Acknowledge
              </Button>
            </Group>
          </Stack>
        )}
      </Modal>
    </Card>
  );
}

export default CLevelAuditDashboard;
